import { Activity, Building2, FileText, HeartPulse, Pill, Stethoscope, UserX, HelpCircle } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { EventDisclosureReport } from '../../types/disclosure';

type QuickStats = NonNullable<EventDisclosureReport['summary']['quickStats']>;

function classifyOutcome(
  text: string | null | undefined,
  formType: string,
  resultOutcomeId?: string | null,
) {
  if (formType === 'paracetamol_screening') return 'treated';
  if (resultOutcomeId) {
    const idMap: Record<string, string> = {
      returned_to_event: 'treated',
      sent_home: 'treated',
      taken_to_hospital: 'hospital',
      referred_gp: 'referred',
      refused_treatment: 'refused',
      other: 'other',
    };
    if (idMap[resultOutcomeId]) return idMap[resultOutcomeId];
  }
  if (!text?.trim()) return 'unspecified';
  const t = text.toLowerCase();
  if (/hospital|a\s*&\s*e|taken to|convey|transport|ambulance|transfer/i.test(t)) return 'hospital';
  if (/refus|declin|no treatment|left (the )?(event|site)|self.?discharg/i.test(t)) return 'refused';
  if (/\bgp\b|advised|refer|specialist|follow.?up/i.test(t)) return 'referred';
  if (/discharg|treated|advice|minor|cleared|returned|on scene|self care|back to event|return to event|event/i.test(t)) {
    return 'treated';
  }
  return 'other';
}

function StatPill({
  label,
  count,
  icon: Icon,
  tone = 'default',
  alwaysShow = false,
}: {
  label: string;
  count: number;
  icon: typeof FileText;
  tone?: 'default' | 'emerald' | 'amber' | 'rose' | 'blue' | 'purple' | 'cyan';
  alwaysShow?: boolean;
}) {
  if (!alwaysShow && count <= 0) return null;
  const tones = {
    default: 'bg-muted/50 border-border text-foreground',
    emerald: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-400',
    amber: 'bg-amber-500/10 border-amber-500/30 text-amber-600 dark:text-amber-400',
    rose: 'bg-rose-500/10 border-rose-500/30 text-rose-600 dark:text-rose-400',
    blue: 'bg-blue-500/10 border-blue-500/30 text-blue-600 dark:text-blue-400',
    purple: 'bg-purple-500/10 border-purple-500/30 text-purple-600 dark:text-purple-400',
    cyan: 'bg-cyan-500/10 border-cyan-500/30 text-cyan-600 dark:text-cyan-400',
  };
  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-xl border px-3 py-2 min-w-[120px]',
        tones[tone],
        count <= 0 && 'opacity-50',
        'print:border-gray-300 print:bg-gray-50 print:text-gray-900'
      )}
    >
      <Icon className="w-4 h-4 shrink-0 opacity-80" />
      <div className="min-w-0">
        <p className="text-lg font-bold leading-none tabular-nums">{count}</p>
        <p className="text-[10px] uppercase tracking-wide opacity-80 mt-0.5 truncate">{label}</p>
      </div>
    </div>
  );
}

/** Derive quick stats from disclosure report data. */
export function deriveQuickStats(report: EventDisclosureReport): QuickStats {
  const prfByType = { standard: 0, minor_injury: 0, paracetamol_screening: 0 };
  const outcomes = { hospital: 0, treated: 0, refused: 0, referred: 0, other: 0, unspecified: 0 };
  const incidentsWithPrf = new Set<string>();

  report.incidents.forEach((inc) => {
    inc.prfs.forEach((prf) => {
      incidentsWithPrf.add(inc.id);
      const ft =
        prf.formType === 'minor_injury'
          ? 'minor_injury'
          : prf.formType === 'paracetamol_screening'
            ? 'paracetamol_screening'
            : 'standard';
      prfByType[ft] += 1;
      outcomes[classifyOutcome(prf.resultingAction, ft, prf.resultOutcomeId)] += 1;
    });

    if (!incidentsWithPrf.has(inc.id)) {
      outcomes[classifyOutcome(inc.closeResult, 'standard')] += 1;
    }
  });

  return {
    prfByType,
    outcomes,
    totalPrfs: report.summary.prfCount,
  };
}

export default function DisclosureQuickStatsBar({ report }: { report: EventDisclosureReport }) {
  const stats = deriveQuickStats(report);
  const { prfByType, outcomes } = stats;
  const hasOutcomes =
    outcomes.hospital +
      outcomes.treated +
      outcomes.refused +
      outcomes.referred +
      outcomes.other +
      outcomes.unspecified >
    0;

  return (
    <section className="mb-8 rounded-2xl border border-border bg-card/50 p-4 print:border-gray-300 print:bg-gray-50 break-inside-avoid">
      <div className="flex items-center gap-2 mb-3">
        <Activity className="w-4 h-4 text-primary print:text-purple-700" />
        <h2 className="text-xs font-bold uppercase tracking-wide text-muted-foreground print:text-gray-700">
          Quick reference — reports &amp; outcomes
        </h2>
      </div>

      <div className="mb-4">
        <p className="text-[10px] uppercase text-muted-foreground mb-2 print:text-gray-600">
          Patient reports submitted
        </p>
        <div className="flex flex-wrap gap-2">
          <StatPill label="PRF" count={prfByType.standard} icon={FileText} tone="purple" alwaysShow />
          <StatPill label="Minor injury" count={prfByType.minor_injury} icon={Stethoscope} tone="blue" alwaysShow />
          <StatPill label="Paracetamol" count={prfByType.paracetamol_screening} icon={Pill} tone="cyan" alwaysShow />
        </div>
      </div>

      {hasOutcomes && (
        <div>
          <p className="text-[10px] uppercase text-muted-foreground mb-2 print:text-gray-600">
            Patient status / outcome
          </p>
          <div className="flex flex-wrap gap-2">
            <StatPill label="Returned to event" count={outcomes.treated} icon={HeartPulse} tone="emerald" />
            <StatPill label="Taken to hospital" count={outcomes.hospital} icon={Building2} tone="rose" />
            <StatPill label="Advised to GP" count={outcomes.referred} icon={Stethoscope} tone="blue" />
            <StatPill label="Refused care" count={outcomes.refused} icon={UserX} tone="amber" />
            <StatPill label="Other outcome" count={outcomes.other} icon={HelpCircle} tone="default" />
            <StatPill label="No status recorded" count={outcomes.unspecified} icon={HelpCircle} tone="default" />
          </div>
        </div>
      )}

      <p className="text-[10px] text-muted-foreground mt-3 print:text-gray-600">
        Paracetamol screenings count as returned to event. Other outcomes are taken from PRF notes and incident close
        records. See full detail below.
      </p>
    </section>
  );
}
